/**
 * Address state for the manual stop form: autocomplete selection, apt/unit,
 * USPS validation and "Did you mean" handling.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocationSearch, type GeocodedResult } from '@/hooks/useLocationSearch';
import { useAddressValidation, toValidationSummary } from '@/hooks/useAddressValidation';
import { toStateCode } from '@/lib/utils/usStates';
import type { AddressValidationSummary, StopKind } from '@/lib/validation/manualStopValidation';
import type { AddressValidationRequest, AddressValidationResult } from '@/types/manualStops';
import type { SuggestionChoice } from './AddressVerification';
import { formatStandardized } from './formHelpers';

export interface AddressFields {
  address: string;
  city: string;
  region: string;
  postal_code?: string;
  lat: number;
  lng: number;
}

const UNIT_DEBOUNCE_MS = 600;

/** Verify customer homes and street-address picks; skip businesses picked by name. */
export function shouldValidateAddress(
  stopKind: StopKind,
  selectedFrom: 'name' | 'address' | null
): boolean {
  return !(stopKind === 'business' && selectedFrom === 'name');
}

const ADDRESS_NOT_CHECKABLE: AddressValidationResult = {
  status: 'unavailable',
  standardized: null,
  geocode: null,
  dpv: null,
  residential: null,
  business: null,
  vacant: null,
  corrected_components: [],
  missing_components: [],
  response_id: null
};

function fieldsFromResult(result: GeocodedResult): AddressFields {
  const { houseNumber, road, city, state, postcode } = result.addressComponents;
  const street =
    houseNumber && road ? `${houseNumber} ${road}` : result.displayName.split(',')[0] || '';
  return {
    address: street.trim(),
    city: city?.trim() ?? '',
    region: toStateCode(state),
    lat: result.lat,
    lng: result.lng,
    ...(postcode?.trim() && { postal_code: postcode.trim() })
  };
}

function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

// eslint-disable-next-line max-lines-per-function -- single state machine for the address step
export function useManualStopAddress(stopKind: StopKind) {
  const search = useLocationSearch();
  const [fields, setFields] = useState<AddressFields | null>(null);
  const [unit, setUnit] = useState('');
  const [suggestionChoice, setSuggestionChoice] = useState<SuggestionChoice>('pending');
  const debouncedUnit = useDebouncedValue(unit.trim(), UNIT_DEBOUNCE_MS);

  // A new autocomplete selection replaces the address and clears earlier decisions.
  useEffect(() => {
    setFields(search.selected ? fieldsFromResult(search.selected) : null);
    setSuggestionChoice('pending');
  }, [search.selected]);

  // The address-validate edge function requires street, city, 2-letter state and ZIP.
  // A business picked from the business-name search is a real, listed business — checking
  // it again adds cost and noise. Customer homes, and businesses found by street address,
  // are verified.
  const shouldValidate = shouldValidateAddress(stopKind, search.selectedFrom);
  const isComplete =
    !!fields?.address && !!fields.city && /^[A-Z]{2}$/.test(fields.region) && !!fields.postal_code;

  const request = useMemo<AddressValidationRequest | undefined>(() => {
    if (!fields || !isComplete || !shouldValidate) return undefined;
    return {
      address_line1: fields.address,
      city: fields.city,
      region: fields.region,
      postal_code: fields.postal_code as string,
      ...(debouncedUnit && { address_line2: debouncedUnit })
    };
  }, [fields, isComplete, shouldValidate, debouncedUnit]);

  const validation = useAddressValidation(request);
  // A picked address without a ZIP or state can't be checked — say so instead of waiting.
  const result = fields && shouldValidate && !isComplete ? ADDRESS_NOT_CHECKABLE : validation.data;
  const isChecking = !!request && (validation.isFetching || unit.trim() !== debouncedUnit);

  const acceptSuggestion = useCallback(() => {
    const s = result?.standardized;
    if (!s || !fields) return;
    setFields({
      address: s.line1,
      city: s.city,
      region: s.region,
      postal_code: s.zip4 ? `${s.zip5}-${s.zip4}` : s.zip5,
      lat: result.geocode?.lat ?? fields.lat,
      lng: result.geocode?.lng ?? fields.lng
    });
    if (s.line2) setUnit(s.line2);
    search.setAddress(formatStandardized({ ...s, line2: null }));
    setSuggestionChoice('accepted');
  }, [result, fields, search]);

  const keepEntered = useCallback(() => setSuggestionChoice('kept'), []);

  /** Summary to store on the stop; built at submit time so validated_at is accurate. */
  const buildSummary = useCallback(
    (): AddressValidationSummary | undefined =>
      result && !isChecking ? toValidationSummary(result) : undefined,
    [result, isChecking]
  );

  const reset = useCallback(() => {
    search.reset();
    setFields(null);
    setUnit('');
    setSuggestionChoice('pending');
  }, [search]);

  return {
    search,
    fields,
    unit,
    setUnit,
    suggestionChoice,
    acceptSuggestion,
    keepEntered,
    result,
    isChecking,
    buildSummary,
    reset
  };
}

export type ManualStopAddressState = ReturnType<typeof useManualStopAddress>;
